<template>
  <div>
    <h1>Þýðingar</h1>

    <v-data-table
      :headers="headers"
      :items="tableItems"
      class="elevation-1"
      :search="search"
    >
      <template #top>
        <div class="d-flex align-center w-100">
          <v-spacer />
          <v-text-field v-model="search" label="Leita" class="mx-4" />
          <v-btn color="primary" class="mb-2" @click="openCreate">Bæta við</v-btn>
        </div>
      </template>

      <template #item.action="{ item }">
        <v-btn size="small" variant="text" @click="editItem(item)">Breyta</v-btn>
        <v-btn size="small" variant="text" @click="confirmDelete(item)">Eyða</v-btn>
      </template>
    </v-data-table>

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="640">
      <v-card>
        <v-card-title class="text-h6">{{ formTitle }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="editedItem.name_Identitfier" label="Auðkenni" />
          <v-textarea v-model="editedItem.isl" label="ÍSL" auto-grow />
          <v-textarea v-model="editedItem.en" label="ENSKA" auto-grow />
          <v-textarea v-model="editedItem.pl" label="PÓLSKA" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="close">Hætta við</v-btn>
          <v-btn variant="text" @click="save">Vista</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.open"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top right"
    >
      {{ snackbar.text }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useTranslationStore } from '@/stores/useTranslationStore'

type Translation = {
  id?: string  // Guid from backend
  name_Identitfier: string
  isl: string
  en: string
  pl: string
}

const translationStore = useTranslationStore()
const { translations, translationItems, loading } = storeToRefs(translationStore)
const { setTranslations, addTranslation, updateTranslation, deleteTranslation } = translationStore

// local derived list for data-table (convert the record of locales into row objects)
const tableItems = ref<Translation[]>([])

onMounted(async () => {
  await loadTranslations()
})

async function loadTranslations() {
  await setTranslations()
  // Map the raw translation items to table format
  tableItems.value = translationItems.value.map(item => ({
    id: item.id,
    name_Identitfier: item.name_Identifier || item.name_Identitfier,  // Handle both spellings
    isl: item.isl || item['isl'] || '',
    en: item.en || item['en'] || '',
    pl: item.pl || item['pl'] || '',
  }))
}

const headers = [
  { title: 'Auðkenni', value: 'name_Identitfier' },
  { title: 'ÍSL', value: 'isl' },
  { title: 'EN', value: 'en' },
  { title: 'PL', value: 'pl' },
  { title: 'Actions', value: 'action', sortable: false },
]

const search = ref('')
const dialog = ref(false)
const editedIndex = ref(-1)
const editedItem = ref<Translation>({
  name_Identitfier: '',
  isl: '',
  en: '',
  pl: '',
})
const defaultItem: Translation = { name_Identitfier: '', isl: '', en: '', pl: '' }

const formTitle = computed(() => (editedIndex.value === -1 ? 'Ný þýðing' : 'Breyta þýðingu'))

function openCreate() {
  editedIndex.value = -1
  editedItem.value = { ...defaultItem }
  dialog.value = true
}

function editItem(item: Translation) {
  editedIndex.value = tableItems.value.indexOf(item)
  editedItem.value = {
    ...item,
    id: item.id,
  }
  dialog.value = true
}

async function confirmDelete(item: Translation) {
  if (!confirm('Ertu viss um að þú viljir eyða?')) return
  try {
    const payload = { id: item.id }
    await deleteTranslation(payload)
    await loadTranslations()
    ok('Vistað')
  } catch {
    err('Villa kom upp vinsamlegast endurhlaða vafra')
  }
}

function close() {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = { ...defaultItem }
    editedIndex.value = -1
  }, 200)
}

async function save() {
  const payload = { ...editedItem.value }
  try {
    if (editedItem.value.id) {
      const updatePayload = { ...payload, id: editedItem.value.id }
      await updateTranslation(updatePayload)
    } else {
      const { id, ...createPayload } = payload
      await addTranslation(createPayload)
    }
    await loadTranslations()
    ok('Vistað')
    close()
  } catch {
    err('Villa kom upp vinsamlegast endurhlaða vafra')
  }
}

const snackbar = ref({
  open: false,
  text: 'Vistað',
  color: 'success' as 'success' | 'error',
  timeout: 3000,
})
function ok(text: string) {
  snackbar.value = { open: true, text, color: 'success', timeout: 3000 }
}
function err(text: string) {
  snackbar.value = { open: true, text, color: 'error', timeout: 4000 }
}
</script>
